/** Ctrl-K / Cmd-K command palette.
 *  Searches stations, navigates to common routes. Mounted globally in App.js. */
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { T } from "@/lib/theme";

const C = T.dark;
const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const ROUTES = [
  { title: "Listen to live radio", path: "/listen", group: "Navigate" },
  { title: "Open my Dashboard", path: "/app/dashboard", group: "Navigate" },
  { title: "AdVerify console", path: "/app/adverify", group: "Navigate" },
  { title: "Airtime Planning Tool", path: "/app/planning", group: "Navigate" },
  { title: "Map dashboard", path: "/app/map", group: "Navigate" },
  { title: "Claim a station", path: "/claim", group: "Navigate" },
  { title: "Account settings", path: "/app/settings", group: "Navigate" },
  { title: "Security & MFA", path: "/app/security", group: "Navigate" },
  { title: "Billing & plans", path: "/app/billing", group: "Navigate" },
  { title: "Admin console", path: "/admin", group: "Navigate" },
  { title: "How we measure", path: "/methodology", group: "Navigate" },
  { title: "Pricing", path: "/pricing", group: "Navigate" },
  { title: "Terms of service", path: "/terms", group: "Navigate" },
  { title: "Privacy policy", path: "/privacy", group: "Navigate" },
  { title: "Sign in / Sign out", path: "/login", group: "Navigate" },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const [stations, setStations] = useState([]);
  const inputRef = useRef(null);
  const nav = useNavigate();

  // Global Ctrl/Cmd-K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open && stations.length === 0) {
      axios.get(`${API_BASE}/public/stations?limit=200`).then((r) => setStations(r.data.stations || [])).catch(() => {});
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open, stations.length]);

  const ql = q.trim().toLowerCase();
  const routeMatches = ql ? ROUTES.filter((r) => r.title.toLowerCase().includes(ql)) : ROUTES;
  const stationMatches = ql
    ? stations.filter((s) => s.name.toLowerCase().includes(ql) || (s.frequency || "").toLowerCase().includes(ql)).slice(0, 6)
    : [];
  const flat = [
    ...stationMatches.map((s) => ({ kind: "station", id: s.id, title: s.name, sub: `${s.frequency || ""} · ${s.country}`, path: `/stations/${s.id}` })),
    ...routeMatches.map((r) => ({ kind: "route", title: r.title, path: r.path, sub: r.group })),
  ];

  const run = (item) => {
    if (!item) return;
    setOpen(false);
    setQ("");
    setIdx(0);
    nav(item.path);
  };

  if (!open) return null;
  return (
    <div data-testid="cmdk" onClick={() => setOpen(false)}
      style={{ position: "fixed", inset: 0, background: "#000a", backdropFilter: "blur(4px)",
        display: "flex", justifyContent: "center", paddingTop: "12vh", zIndex: 9999 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: "min(560px, 92vw)", background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 14, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
        <input ref={inputRef} data-testid="cmdk-input" value={q}
          onChange={(e) => { setQ(e.target.value); setIdx(0); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, flat.length - 1)); }
            else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
            else if (e.key === "Enter") { e.preventDefault(); run(flat[idx]); }
          }}
          placeholder="Search stations, pages, anything…"
          style={{ width: "100%", padding: "18px 22px", background: "transparent", border: "none",
            color: C.text, fontFamily: "inherit", fontSize: 16, outline: "none",
            borderBottom: `1px solid ${C.border}` }} />
        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {flat.length === 0 && (
            <div style={{ color: C.muted, fontSize: 13, padding: "20px 22px", textAlign: "center" }}>No matches.</div>
          )}
          {flat.map((item, i) => (
            <div key={`${item.kind}-${item.title}-${i}`} data-testid={`cmdk-item-${i}`}
              onMouseEnter={() => setIdx(i)} onClick={() => run(item)}
              style={{ padding: "11px 22px", cursor: "pointer",
                background: idx === i ? C.bg : "transparent",
                borderLeft: `3px solid ${idx === i ? T.greenAccent : "transparent"}`,
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 600 }}>
                  {item.kind === "station" ? "📻 " : "→ "}{item.title}
                </div>
                <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{item.sub}</div>
              </div>
              <div style={{ color: C.muted, fontSize: 10, fontFamily: "monospace" }}>{item.path}</div>
            </div>
          ))}
        </div>
        <div style={{ padding: "10px 22px", borderTop: `1px solid ${C.border}`, background: C.bg,
          color: C.muted, fontSize: 11, display: "flex", gap: 16, fontFamily: "monospace" }}>
          <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
          <span style={{ marginLeft: "auto" }}>⌘K / Ctrl-K</span>
        </div>
      </div>
    </div>
  );
}
